<template>
  <teleport to="body">
    <div class="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm pt-safe">
      <transition-group name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="glass-surface toast-item rounded-lg shadow-lg p-4 flex items-start gap-3 min-w-[320px] border-l-4 backdrop-blur-sm"
          :class="toastClasses(toast.type)">
          <span class="material-symbols-outlined flex-shrink-0" :class="iconClasses(toast.type)">
            {{ getIcon(toast.type) }}
          </span>
          <div class="flex-1">
            <p class="text-sm font-medium" :class="textClasses()">
              {{ toast.message }}
            </p>
          </div>
          <button
            @click="removeToast(toast.id)"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <span class="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useToastStore } from '../stores/toast';

const toastStore = useToastStore();
const { toasts } = storeToRefs(toastStore);
const { removeToast } = toastStore;

function toastClasses(type: string) {
  const baseClasses = 'bg-white/95 dark:bg-gray-800/95';
  const borderClasses = {
    success: 'border-green-500',
    error: 'border-red-500',
    warning: 'border-yellow-500',
    info: 'border-blue-500',
  };
  return `${baseClasses} ${borderClasses[type as keyof typeof borderClasses] || borderClasses.info}`;
}

function iconClasses(type: string) {
  const colors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500',
  };
  return colors[type as keyof typeof colors] || colors.info;
}

function textClasses() {
  return 'text-gray-800 dark:text-gray-200';
}

function getIcon(type: string) {
  const icons = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    info: 'info',
  };
  return icons[type as keyof typeof icons] || icons.info;
}
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-item {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active {
    transition: none;
  }

  .toast-item {
    animation: none;
  }
}
</style>
